import { useEffect, useMemo, useState } from 'react'
import { X, Loader2, Banknote, AlertTriangle, FileDown, Download } from 'lucide-react'
import { apiGet } from '../../lib/api.js'
import { fmtMonthTH } from '../../lib/timetable.js'
import { fmtMoney } from '../../lib/salary.js'
import { buildTransferFiles, money, ISSUE_LABEL } from '../../lib/scbBulk.js'
import { zipText, downloadBlob } from '../../lib/zip.js'

const FIELD = 'w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-[13.5px] text-slate-800 outline-none focus:border-brand-400 focus:ring-4 focus:ring-brand-500/10'
const LABEL = 'block text-[12.5px] font-medium text-slate-500 mb-1'

const todayISO = () => {
  const d = new Date()
  const p = (n) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`
}

/**
 * ไฟล์โอนเงิน SCB — the ten SCB Business Net bulk-transfer files for a salary
 * run, zipped.
 *
 * The split is Branch.Company (via Employee.branch_acc_id) × ไทยพาณิชย์-or-not:
 * five companies, two products, ten files. Each file debits that company's own
 * SCB account, so they can never be merged. ไทยพาณิชย์ → 3PT, everything else
 * → RFT — the same pair the doctor-fee files have always used.
 *
 * The value date is asked for here rather than defaulted silently: it is the day
 * the money actually leaves, and SCB will reject a date in the past.
 *
 * The ledger's branch/employee filters ARE honoured (a re-run of one company is
 * a real need). The ประเภทรายการ and เฉพาะรายการที่เพิ่มเอง filters are NOT —
 * a transfer must be the employee's NET, and a filtered subtotal is not that.
 */
export default function ScbTransferModal({ year, month, branchId, employeeId, branchName, onClose }) {
  const [valueDate, setValueDate] = useState(todayISO)
  const [loading, setLoading] = useState(true)
  const [rows, setRows] = useState([])
  const [err, setErr] = useState('')
  const [busy, setBusy] = useState(false)

  const today = todayISO()
  const pastDate = valueDate < today

  useEffect(() => {
    let alive = true
    setLoading(true); setErr('')
    const q = new URLSearchParams({ year: String(year), month: String(month) })
    if (branchId) q.set('branch_id', String(branchId))
    if (employeeId) q.set('employee_id', String(employeeId))
    apiGet(`/api/hr/salary-scb?${q}`)
      .then((res) => { if (alive) setRows(Array.isArray(res?.rows) ? res.rows : []) })
      .catch((e) => { if (alive) setErr(String(e.message || e)) })
      .finally(() => { if (alive) setLoading(false) })
    return () => { alive = false }
  }, [year, month, branchId, employeeId])

  // Preview only — the refs are regenerated at download so every submitted file
  // carries fresh transaction references.
  const preview = useMemo(
    () => buildTransferFiles({ rows, year, month, valueDate }),
    [rows, year, month, valueDate],
  )

  function download() {
    if (pastDate || !preview.files.length) return
    setBusy(true)
    try {
      const built = buildTransferFiles({ rows, year, month, valueDate })
      const blob = zipText(built.files.map((f) => ({ name: f.name, text: f.text })))
      downloadBlob(blob, `SCB_salary_${year}-${String(month).padStart(2, '0')}_${valueDate}.zip`)
      onClose()
    } catch (e) { setErr(String(e.message || e)) }
    finally { setBusy(false) }
  }

  const scope = [
    branchId ? `สาขา ${branchName || branchId}` : null,
    employeeId ? 'เฉพาะพนักงานที่เลือก' : null,
  ].filter(Boolean).join(' · ')

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-[2px]" onMouseDown={onClose} />
      <div className="relative w-full max-w-[560px] bg-white rounded-2xl shadow-2xl ring-1 ring-slate-200 animate-ss-pop-in max-h-[88vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <div className="flex items-center gap-2 min-w-0">
            <Banknote size={17} className="text-brand-600" />
            <h2 className="text-[15.5px] font-semibold text-slate-900">ไฟล์โอนเงิน SCB</h2>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg text-slate-400 hover:bg-slate-100 shrink-0"><X size={18} /></button>
        </div>

        <div className="px-5 py-4 overflow-y-auto">
          <div className="mb-4">
            <label className={LABEL}>วันที่ต้องการโอนเงิน (Value Date)</label>
            <input
              type="date" value={valueDate} min={today}
              onChange={(e) => setValueDate(e.target.value)}
              className={`${FIELD} ${pastDate ? 'border-rose-300' : ''}`}
            />
            {pastDate
              ? <p className="text-[12px] text-rose-500 mt-1">วันที่โอนต้องไม่ย้อนหลัง — ธนาคารจะปฏิเสธไฟล์</p>
              : <p className="text-[12px] text-slate-400 mt-1">
                  รอบเงินเดือน {fmtMonthTH(year, month)}{scope ? ` · ${scope}` : ''}
                </p>}
          </div>

          {loading && (
            <div className="py-8 text-center text-slate-400">
              <Loader2 size={22} className="animate-spin mx-auto mb-2" /> กำลังดึงรายการ…
            </div>
          )}

          {err && <div className="mb-3 text-[13px] text-rose-500">{err}</div>}

          {!loading && !err && (
            <>
              {preview.skipped.length > 0 && (
                <div className="mb-4 rounded-xl bg-amber-50 ring-1 ring-amber-200 px-4 py-3">
                  <div className="flex items-center gap-1.5 text-[13px] font-semibold text-amber-800 mb-1.5">
                    <AlertTriangle size={14} /> ข้ามไป {preview.skipped.length} คน
                  </div>
                  <ul className="text-[12.5px] text-amber-800/90 space-y-0.5">
                    {preview.skipped.map((s) => (
                      <li key={s.employee_id}>
                        {s.code || s.name} — {ISSUE_LABEL[s.issue] || s.issue}
                        {s.issue === 'net_not_positive' ? ` (${fmtMoney(Number(s.net))})` : ''}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {preview.files.length === 0 ? (
                <div className="py-6 text-center text-[13px] text-slate-400">
                  ไม่มีรายการที่โอนได้ในรอบนี้
                </div>
              ) : (
                <div className="rounded-xl ring-1 ring-slate-200 overflow-hidden">
                  <table className="w-full text-[12.5px]">
                    <thead className="bg-slate-50 text-slate-400">
                      <tr>
                        <th className="text-left font-medium px-3 py-2">ไฟล์</th>
                        <th className="text-left font-medium px-3 py-2">บัญชีตัดเงิน</th>
                        <th className="text-right font-medium px-3 py-2">คน</th>
                        <th className="text-right font-medium px-3 py-2">ยอดโอน</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {preview.files.map((f) => (
                        <tr key={f.name}>
                          <td className="px-3 py-2">
                            <div className="flex items-center gap-1.5 text-slate-700">
                              <FileDown size={13} className="text-slate-300 shrink-0" />
                              <span className="truncate">{f.name}</span>
                              <span className="text-[10.5px] font-medium text-slate-400 ring-1 ring-slate-200 rounded px-1 shrink-0">{f.productCode}</span>
                            </div>
                            <div className="text-[11px] text-slate-400 truncate pl-[19px]">{f.companyName}</div>
                          </td>
                          <td className="px-3 py-2 text-slate-500 tabular-nums">{f.debitAccount}</td>
                          <td className="px-3 py-2 text-right text-slate-500 tabular-nums">{f.count}</td>
                          <td className="px-3 py-2 text-right font-medium text-slate-800 tabular-nums">{money(f.total)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="bg-slate-50">
                      <tr>
                        <td className="px-3 py-2 font-semibold text-slate-700" colSpan={2}>
                          รวม {preview.files.length} ไฟล์
                        </td>
                        <td className="px-3 py-2 text-right font-semibold text-slate-700 tabular-nums">
                          {preview.files.reduce((n, f) => n + f.count, 0)}
                        </td>
                        <td className="px-3 py-2 text-right font-semibold text-slate-900 tabular-nums">
                          {money(preview.grandTotal)}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
            </>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-3.5 border-t border-slate-100">
          <button onClick={onClose}
            className="px-3.5 py-2 rounded-xl text-[13px] font-medium text-slate-600 hover:bg-slate-100">
            ยกเลิก
          </button>
          <button onClick={download} disabled={busy || loading || pastDate || !preview.files.length}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-[13px] font-semibold text-white bg-brand-600 hover:bg-brand-700 disabled:opacity-50">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Download size={15} />}
            ดาวน์โหลด {preview.files.length || ''} ไฟล์ (.zip)
          </button>
        </div>
      </div>
    </div>
  )
}
